import React, { useEffect, useState } from "react";
import Api from "../../../../shared/api/api";
import "../styles/AdminOrders.css";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [trackingInput, setTrackingInput] = useState({});
  const [message, setMessage] = useState("");

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await Api.getAllOrders();
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => setFilter(e.target.value);

  /* =============== SORTING =============== */
  const handleSort = (column) => {
    if (sortColumn === column)
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortOrders = (orders) => {
    if (!sortColumn) return orders;

    return [...orders].sort((a, b) => {
      let A = a[sortColumn] ?? "";
      let B = b[sortColumn] ?? "";

      if (sortColumn === "created_at") {
        return sortDirection === "asc"
          ? new Date(A) - new Date(B)
          : new Date(B) - new Date(A);
      }

      if (A < B) return sortDirection === "asc" ? -1 : 1;
      if (A > B) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  let filtered = orders.filter((o) =>
    filter === "all" ? true : o.type === filter
  );
  filtered = sortOrders(filtered);

  /* =============== TRACKING INPUT =============== */
  const handleTrackingChange = (orderNum, field, value) => {
    setTrackingInput((prev) => ({
      ...prev,
      [orderNum]: {
        ...(prev[orderNum] || {}),
        [field]: value,
      },
    }));
  };

  const saveTracking = async (orderNum) => {
    try {
      const data = trackingInput[orderNum] || {};
      const tracking_number = data.tracking_number?.trim();
      const shipped_at = data.shipped_at || null;

      if (!tracking_number) {
        alert("Tracking number cannot be empty.");
        return;
      }

      await Api.updateBookOrderTracking(orderNum, {
        order_number: orderNum,
        tracking_number,
        shipped_at,
      });

      setMessage("Tracking updated!");
      setTimeout(() => setMessage(""), 2500);

      await fetchOrders();
    } catch (err) {
      console.error("Tracking error:", err);
      alert("Failed to update tracking.");
    }
  };

  /* =============== MERCH RESEND =============== */
  const resendPrintify = async (orderId) => {
    try {
      await Api.request(`orders/${orderId}/retry`, {}, "post");
      setMessage("Printify resend triggered!");
      setTimeout(() => setMessage(""), 2500);
      await fetchOrders();
    } catch (err) {
      console.error(err);
      alert("Error resending Printify order.");
    }
  };

  /* =============== CAMELCASE HELPER =============== */
  const camelName = (name) => {
    if (!name) return "—";
    return name
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1))
      .join(" ");
  };

  const formatter = (date) => {
    let month = date.slice(5,7);
    let day = date.slice(8,10);
    let year = date.slice(0,4);
    return `${month}-${day}-${year}`
  }

  return (
    <div className="adminOrdersContainer">
      <h1 className="ordersHeader">Orders</h1>

      {message && <div className="statusMessage">{message}</div>}

      <div className="adminOrdersFilters">
        <label className="filterLabel">Filter:</label>
        <select value={filter} onChange={handleFilterChange}>
          <option value="all">All Orders</option>
          <option value="book">Book Orders</option>
          <option value="merch">Merch Orders</option>
        </select>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="adminOrdersTable">
          <thead>
            <tr>
              <th onClick={() => handleSort("type")}>Type</th>
              <th onClick={() => handleSort("created_at")}>Create Date</th>
              <th onClick={() => handleSort("order_number")}>Order #</th>
              <th onClick={() => handleSort("name")}>Name</th>
              <th onClick={() => handleSort("email")}>Email</th>
              <th>Details</th>
              <th onClick={() => handleSort("delivery_status")}>Status</th>
              <th>Tracking</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((order) => {
              const orderNum = order.order_number;

              return (
                <tr key={`${order.type}-${orderNum}`}>
                  <td className="orderTypeCell">
                    {order.type === "book" ? (
                      <span className="badgeBook">BOOK</span>
                    ) : (
                      <span className="badgeMerch">MERCH</span>
                    )}
                  </td>

                  <td className="dateTypeCell">
                    {formatter(order.created_at)}
                  </td>    

                  <td>{orderNum}</td>

                  {/* NAME */}
                  <td>{camelName(order.name)}</td>

                  {/* EMAIL */}
                  <td>{order.email || "—"}</td>

                  {/* DETAILS */}
                  <td>
                    {order.type === "book" ? (
                      <>
                        <strong>{order.book_title}</strong>
                        <br />
                        {order.format}
                        <br />${order.price_paid}
                      </>
                    ) : (
                      <>
                        {order.items?.map((i, idx) => (
                          <div key={idx}>
                            {i.product_name} (x{i.quantity})
                          </div>
                        )) || "—"}
                      </>
                    )}
                  </td>

                  {/* STATUS */}
                  <td>{order.delivery_status}</td>

                  {/* TRACKING COLUMN */}
                  <td className="trackingColumn">
                    {order.format === "ebook" ? (
                      "—"
                    ) : order.tracking_sent === 1 ? (
                      <>
                        {order.tracking_number}
                        <br />
                        <span className="trackingSentBadge">✓ Sent</span>
                      </>
                    ) : (
                      order.tracking_number || "—"
                    )}
                  </td>

                  {/* ADDRESS */}
                  <td className="addressWide">
                    {order.type === "book" && order.address?.address_line1 ? (
                      <>
                        {order.address.address_line1}
                        <br />
                        {order.address.address_line2 && (
                          <>
                            {order.address.address_line2}
                            <br />
                          </>
                        )}
                        {order.address.city}, {order.address.state}{" "}
                        {order.address.postal_code}
                        <br />
                        {order.address.country}
                      </>
                    ) : order.address ? (
                      <>
                        {order.address.address1}
                        <br />
                        {order.address.address2}
                        <br />
                        {order.address.city}, {order.address.state}{" "}
                        {order.address.postal_code}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>

                  {/* ACTIONS */}
                  <td className="actionsCell">
                    {order.type !== "book" ? (
                      <button
                        className="resendBtn"
                        onClick={() => resendPrintify(order.id)}
                      >
                        Resend Printify
                      </button>
                    ) : order.format === "ebook" ? (
                      <span className="noTracking">N/A</span>
                    ) : order.tracking_sent === 1 ? (
                      <>
                        <span className="trackingSentBadge">
                          ✓ Tracking Email Sent
                        </span>
                      </>
                    ) : (
                      <div className="trackingControls">
                        <input
                          type="text"
                          placeholder="Tracking #"
                          value={
                            trackingInput[orderNum]?.tracking_number || ""
                          }
                          onChange={(e) =>
                            handleTrackingChange(
                              orderNum,
                              "tracking_number",
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="date"
                          value={trackingInput[orderNum]?.shipped_at || ""}
                          onChange={(e) =>
                            handleTrackingChange(
                              orderNum,
                              "shipped_at",
                              e.target.value
                            )
                          }
                        />

                        <button
                          className="saveTrackingBtn"
                          onClick={() => saveTracking(orderNum)}
                        >
                          Save
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
